import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Animated,
  Dimensions,
  TouchableWithoutFeedback,
  FlatList,
  useWindowDimensions,
} from 'react-native';
import { Colors } from '@/constants/colors';
import { Ionicons } from '@expo/vector-icons';
import { StockHeader } from './components/StockHeader';
import { IntelligentOutlookTab } from './components/IntelligentOutlookTab';
import { ChartTab } from './components/ChartTab';
import { polygonMarketDataService } from '@/services/api/polygonMarketData.service';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');
const MODAL_HEIGHT = SCREEN_HEIGHT * 0.7; // 70% of screen height

type TabType = 'outlook' | 'chart';

interface DiscussedStock {
  ticker: string;
  companyName: string;
  mentionCount?: number;
}

interface StockDetailModalProps {
  visible: boolean;
  onClose: () => void;
  ticker: string;
  companyName: string;
  groupId?: string;
  initialTab?: TabType;
  discussedStocks?: DiscussedStock[]; // List of stocks from chat
}

export const StockDetailModal: React.FC<StockDetailModalProps> = ({
  visible,
  onClose,
  ticker: initialTicker,
  companyName: initialCompanyName,
  groupId,
  initialTab = 'outlook',
  discussedStocks = [],
}) => {
  const [selectedTicker, setSelectedTicker] = useState(initialTicker);
  const [selectedCompany, setSelectedCompany] = useState(initialCompanyName);
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [stockPrice, setStockPrice] = useState<number | undefined>(undefined);
  const [priceChange, setPriceChange] = useState<number | undefined>(undefined);
  const [priceChangePercent, setPriceChangePercent] = useState<number | undefined>(undefined);
  const [priceLoading, setPriceLoading] = useState(true);
  const [leftPanelWidth] = useState(300);

  const slideAnim = useRef(new Animated.Value(MODAL_HEIGHT)).current;
  const { width } = useWindowDimensions();
  const isLargeScreen = width >= 768;

  // Update selected stock when initial ticker changes
  useEffect(() => {
    setSelectedTicker(initialTicker);
    setSelectedCompany(initialCompanyName);
  }, [initialTicker, initialCompanyName]);

  // Fetch live stock price
  useEffect(() => {
    if (!visible) return;

    const fetchPrice = async () => {
      try {
        setPriceLoading(true);
        console.log(`[StockDetailModal] Fetching price for ${selectedTicker}`);

        const { data, error } = await polygonMarketDataService.getQuote(selectedTicker);

        if (error || !data) {
          console.error(`[StockDetailModal] Failed to fetch price:`, error);
          setPriceLoading(false);
          return;
        }

        setStockPrice(data.price);
        setPriceChange(data.change);
        setPriceChangePercent(data.changePercent);
        console.log(
          `[StockDetailModal] ✅ Price for ${selectedTicker}: $${data.price} (${data.changePercent >= 0 ? '+' : ''}${data.changePercent.toFixed(2)}%)`
        );
      } catch (err) {
        console.error(`[StockDetailModal] Error fetching price:`, err);
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrice();
  }, [selectedTicker, visible]);

  // Slide animation
  useEffect(() => {
    if (visible) {
      Animated.spring(slideAnim, {
        toValue: 0,
        damping: 25,
        stiffness: 200,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: MODAL_HEIGHT,
        duration: 250,
        useNativeDriver: true,
      }).start();
    }
  }, [visible, slideAnim]);

  const handleStockSelect = (ticker: string, companyName: string) => {
    setSelectedTicker(ticker);
    setSelectedCompany(companyName);
    // Reset price data while fetching new stock
    setPriceLoading(true);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'outlook':
        return (
          <IntelligentOutlookTab
            ticker={selectedTicker}
            companyName={selectedCompany}
            groupId={groupId}
          />
        );
      case 'chart':
        return <ChartTab ticker={selectedTicker} groupId={groupId} />;
      default:
        return null;
    }
  };

  const renderStockItem = ({ item }: { item: DiscussedStock }) => {
    const isSelected = selectedTicker === item.ticker;

    return (
      <TouchableOpacity
        style={[styles.stockItem, isSelected && styles.selectedStockItem]}
        onPress={() => handleStockSelect(item.ticker, item.companyName)}
      >
        <View style={styles.stockItemLeft}>
          <Text style={styles.stockItemTicker}>{item.ticker}</Text>
          <Text style={styles.stockItemCompany} numberOfLines={1}>
            {item.companyName}
          </Text>
        </View>
        {item.mentionCount && item.mentionCount > 1 && (
          <View style={styles.mentionBadge}>
            <Text style={styles.mentionCount}>{item.mentionCount}</Text>
          </View>
        )}
      </TouchableOpacity>
    );
  };

  const stockListPanel = (
    <View style={[styles.leftPanel, isLargeScreen && { width: leftPanelWidth }]}>
      <View style={styles.leftPanelHeader}>
        <Text style={styles.leftPanelTitle}>Discussed Stocks</Text>
        <Text style={styles.leftPanelCount}>{discussedStocks.length} stocks</Text>
      </View>

      {discussedStocks.length === 0 ? (
        <View style={styles.emptyStocks}>
          <Ionicons name="trending-up-outline" size={48} color={Colors.text.tertiary} />
          <Text style={styles.emptyStocksText}>No stocks discussed</Text>
        </View>
      ) : (
        <FlatList
          data={discussedStocks}
          renderItem={renderStockItem}
          keyExtractor={item => item.ticker}
          ItemSeparatorComponent={() => <View style={styles.stockSeparator} />}
        />
      )}
    </View>
  );

  const detailPanel = (
    <View style={styles.rightPanel}>
      {/* Stock Price Header */}
      <StockHeader
        ticker={selectedTicker}
        companyName={selectedCompany}
        price={stockPrice}
        change={priceChange}
        changePercent={priceChangePercent}
        isLoading={priceLoading}
      />

      {/* Tab Bar */}
      <View style={styles.tabBar}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'outlook' && styles.activeTab]}
          onPress={() => setActiveTab('outlook')}
        >
          <Ionicons
            name="analytics"
            size={20}
            color={activeTab === 'outlook' ? Colors.primary : Colors.text.tertiary}
          />
          <Text style={[styles.tabText, activeTab === 'outlook' && styles.activeTabText]}>
            Outlook
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, activeTab === 'chart' && styles.activeTab]}
          onPress={() => setActiveTab('chart')}
        >
          <Ionicons
            name="bar-chart"
            size={20}
            color={activeTab === 'chart' ? Colors.primary : Colors.text.tertiary}
          />
          <Text style={[styles.tabText, activeTab === 'chart' && styles.activeTabText]}>Chart</Text>
        </TouchableOpacity>
      </View>

      {/* Tab Content */}
      <View style={styles.tabContent}>{renderTabContent()}</View>
    </View>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.modalContainer}>
        {/* Backdrop */}
        <TouchableWithoutFeedback onPress={onClose}>
          <View style={styles.backdrop} />
        </TouchableWithoutFeedback>

        {/* Modal Content */}
        <Animated.View
          style={[
            styles.modalContent,
            {
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          {/* Drag Handle */}
          <View style={styles.dragHandleContainer}>
            <View style={styles.dragHandle} />
          </View>

          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Stock Analysis</Text>
            <TouchableOpacity style={styles.closeButton} onPress={onClose}>
              <Ionicons name="close" size={24} color={Colors.text.primary} />
            </TouchableOpacity>
          </View>

          {/* Split Content */}
          {isLargeScreen && discussedStocks.length > 0 ? (
            <View style={styles.splitContainer}>
              {stockListPanel}
              <View style={styles.divider} />
              {detailPanel}
            </View>
          ) : (
            detailPanel
          )}
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContent: {
    height: MODAL_HEIGHT,
    backgroundColor: Colors.background.primary,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    overflow: 'hidden',
  },
  dragHandleContainer: {
    alignItems: 'center',
    paddingVertical: 8,
  },
  dragHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: Colors.border.default,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  closeButton: {
    position: 'absolute',
    right: 16,
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  leftPanel: {
    width: 300,
    borderRightWidth: 1,
    borderRightColor: Colors.border.default,
    backgroundColor: Colors.background.primary,
  },
  leftPanelHeader: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  leftPanelTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 4,
  },
  leftPanelCount: {
    fontSize: 12,
    color: Colors.text.secondary,
  },
  stockItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: Colors.background.primary,
  },
  selectedStockItem: {
    backgroundColor: Colors.background.secondary,
    borderLeftWidth: 3,
    borderLeftColor: Colors.primary,
  },
  stockItemLeft: {
    flex: 1,
  },
  stockItemTicker: {
    fontSize: 15,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  stockItemCompany: {
    fontSize: 12,
    color: Colors.text.secondary,
  },
  mentionBadge: {
    backgroundColor: Colors.primary + '20',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  mentionCount: {
    fontSize: 11,
    fontWeight: '600',
    color: Colors.primary,
  },
  stockSeparator: {
    height: 1,
    backgroundColor: Colors.border.subtle,
    marginLeft: 16,
  },
  emptyStocks: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  emptyStocksText: {
    marginTop: 12,
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
  },
  divider: {
    width: 1,
    backgroundColor: Colors.border.default,
  },
  rightPanel: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: Colors.background.primary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    gap: 6,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: Colors.primary,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.text.tertiary,
  },
  activeTabText: {
    color: Colors.primary,
    fontWeight: '600',
  },
  tabContent: {
    flex: 1,
  },
});
